import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  ConfiguratorState,
  DatacenterConfig,
  DatacenterNode,
  WizardStep,
  WIZARD_STEPS,
  ScenarioType,
  NetworkConfig,
  SecurityConfig,
  ServiceAllocation,
  VPNPeer,
  ValidationError,
  isStepComplete,
  canProceedToStep,
  generateNodeId,
  generatePeerId,
  calculateTotalPower,
  calculateTotalCost,
  NodeType,
  NodeSpecs,
  ComputeRole,
  FormFactor,
  // Container types
  ContainerSize,
  ContainerRack,
  ContainerConfig,
  InfrastructureUnit,
  generateRackId,
  generateInfrastructureId,
  getRackDimensions,
  // Network segment types
  NetworkSegment,
  NetworkSegmentType,
  NodeNetworkAssignment,
  generateSegmentId,
  NETWORK_SEGMENT_PRESETS,
} from '../types/configurator';
import { getInfrastructureById } from '../data/infrastructure';
import {
  SCENARIO_PRESETS,
  DEFAULT_NETWORK_CONFIG,
  DEFAULT_SECURITY_CONFIG,
  DEFAULT_FORM_FACTOR,
} from '../data/scenarios';
import { getDefaultServices, SERVICES } from '../data/services';
import {
  ConfigSnapshot,
  ConfigChange,
  diffConfigs,
  generateSnapshotId,
} from '../utils/configDiff';

// ============================================
// HISTORY CONFIGURATION
// ============================================

const MAX_HISTORY_SIZE = 50; // Maximum number of undo steps

// ============================================
// DEFAULT CONFIG
// ============================================

function createDefaultConfig(): DatacenterConfig {
  return {
    name: 'My Hermit Datacenter',
    description: '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    scenario: 'minimal',
    formFactor: DEFAULT_FORM_FACTOR,
    network: { ...DEFAULT_NETWORK_CONFIG },
    nodes: [],
    serviceAllocations: [],
    security: { ...DEFAULT_SECURITY_CONFIG },
    estimatedCost: 0,
    estimatedPower: 0,
  };
}

// ============================================
// STORE INTERFACE
// ============================================

interface ConfiguratorStore extends ConfiguratorState {
  // Navigation
  goToStep: (step: WizardStep) => void;
  goNext: () => void;
  goBack: () => void;

  // Scenario
  setScenario: (scenario: ScenarioType) => void;
  setFormFactor: (formFactor: FormFactor) => void;

  // Network
  updateNetwork: (updates: Partial<NetworkConfig>) => void;

  // Nodes
  addNode: (type: NodeType, specs?: Partial<NodeSpecs>, role?: ComputeRole) => void;
  updateNode: (id: string, updates: Partial<DatacenterNode>) => void;
  removeNode: (id: string) => void;
  duplicateNode: (id: string) => void;
  reorderNodes: (fromIndex: number, toIndex: number) => void;

  // Services
  allocateService: (serviceId: string, nodeId: string) => void;
  deallocateService: (serviceId: string, nodeId: string) => void;
  toggleService: (serviceId: string, nodeId: string, enabled: boolean) => void;

  // Security
  updateSecurity: (updates: Partial<SecurityConfig>) => void;
  addVPNPeer: (peer: Omit<VPNPeer, 'id'>) => void;
  updateVPNPeer: (id: string, updates: Partial<VPNPeer>) => void;
  removeVPNPeer: (id: string) => void;

  // Container Management (for container form factor)
  initializeContainer: (size: ContainerSize) => void;
  setContainerSize: (size: ContainerSize) => void;
  addContainerRack: (type: 'rack-18' | 'rack-42') => void;
  updateContainerRack: (rackId: string, updates: Partial<ContainerRack>) => void;
  removeContainerRack: (rackId: string) => void;
  moveContainerRack: (rackId: string, position: { x: number; y: number }) => void;
  assignNodeToRack: (nodeId: string, rackId: string) => void;
  unassignNodeFromRack: (nodeId: string) => void;
  addInfrastructure: (catalogId: string) => void;
  updateInfrastructure: (id: string, updates: Partial<InfrastructureUnit>) => void;
  removeInfrastructure: (id: string) => void;
  moveInfrastructure: (id: string, position: { x: number; y: number }) => void;

  // Network Segment Management
  addNetworkSegment: (type: NetworkSegmentType) => void;
  updateNetworkSegment: (id: string, updates: Partial<NetworkSegment>) => void;
  removeNetworkSegment: (id: string) => void;
  assignNodeToNetwork: (nodeId: string, segmentId: string, ip: string, isPrimary?: boolean) => void;
  unassignNodeFromNetwork: (nodeId: string, segmentId: string) => void;
  updateNodeNetworkIP: (nodeId: string, segmentId: string, ip: string) => void;
  setSubscriptionModel: (enabled: boolean, providerName?: string, customerName?: string) => void;

  // General
  updateConfig: (updates: Partial<DatacenterConfig>) => void;
  loadConfig: (config: DatacenterConfig) => void;
  reset: () => void;
  validate: () => ValidationError[];
  getStepErrors: (step: WizardStep) => ValidationError[];

  // History / Undo-Redo
  history: ConfigSnapshot[];
  historyIndex: number;
  undo: () => void;
  redo: () => void;
  getLastChanges: () => ConfigChange[];
  createSnapshot: (label?: string) => void;
}

// ============================================
// SELECTORS (computed values derived from state)
// ============================================

export function selectCanGoNext(state: ConfiguratorStore): boolean {
  const currentIndex = WIZARD_STEPS.indexOf(state.currentStep);
  if (currentIndex >= WIZARD_STEPS.length - 1) return false;
  return isStepComplete(state.currentStep, state.config);
}

export function selectCanGoBack(state: ConfiguratorStore): boolean {
  return WIZARD_STEPS.indexOf(state.currentStep) > 0;
}

export function selectIsFirstStep(state: ConfiguratorStore): boolean {
  return state.currentStep === WIZARD_STEPS[0];
}

export function selectIsLastStep(state: ConfiguratorStore): boolean {
  return state.currentStep === WIZARD_STEPS[WIZARD_STEPS.length - 1];
}

export function selectCurrentStepIndex(state: ConfiguratorStore): number {
  return WIZARD_STEPS.indexOf(state.currentStep);
}

export function selectCanUndo(state: ConfiguratorStore): boolean {
  return state.historyIndex > 0;
}

export function selectCanRedo(state: ConfiguratorStore): boolean {
  return state.historyIndex < state.history.length - 1;
}

// ============================================
// NODE CREATION HELPERS
// ============================================

function createNode(
  type: NodeType,
  nodes: DatacenterNode[],
  network: NetworkConfig,
  specs?: Partial<NodeSpecs>,
  role?: ComputeRole
): DatacenterNode {
  const typeCount = nodes.filter((n) => n.type === type).length;
  const baseIP = network.gateway;
  const parts = baseIP.split('.');

  let ipOffset: number;
  let baseName: string;
  let defaultSpecs: NodeSpecs;

  switch (type) {
    case 'gateway':
      ipOffset = 1;
      baseName = 'gw';
      defaultSpecs = { cpuCores: 4, memoryGB: 4, storageGB: 64, powerWatts: 5 };
      break;
    case 'compute':
      ipOffset = 10 + typeCount;
      baseName = 'node';
      defaultSpecs = { cpuCores: 4, memoryGB: 8, storageGB: 128, powerWatts: 15 };
      break;
    case 'storage':
      ipOffset = 20 + typeCount;
      baseName = 'stor';
      defaultSpecs = { cpuCores: 4, memoryGB: 8, storageGB: 4000, powerWatts: 25 };
      break;
    case 'daq':
      ipOffset = 30 + typeCount;
      baseName = 'daq';
      defaultSpecs = { cpuCores: 2, memoryGB: 2, storageGB: 32, powerWatts: 10 };
      break;
  }

  const nodeNum = typeCount + 1;
  const hostname = `${baseName}${nodeNum.toString().padStart(2, '0')}`;

  return {
    id: generateNodeId(),
    name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${nodeNum}`,
    hostname,
    type,
    ip: `${parts[0]}.${parts[1]}.${parts[2]}.${ipOffset}`,
    specs: { ...defaultSpecs, ...specs },
    role,
    services: [],
    enabled: true,
  };
}

// ============================================
// STORE IMPLEMENTATION
// ============================================

// Helper to save snapshot before mutation
function saveSnapshot(
  state: { config: DatacenterConfig; history: ConfigSnapshot[]; historyIndex: number },
  label?: string
): { history: ConfigSnapshot[]; historyIndex: number } {
  const snapshot: ConfigSnapshot = {
    id: generateSnapshotId(),
    timestamp: Date.now(),
    config: JSON.parse(JSON.stringify(state.config)), // Deep clone
    label,
  };

  // Truncate any redo history
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  newHistory.push(snapshot);

  // Limit history size
  if (newHistory.length > MAX_HISTORY_SIZE) {
    newHistory.shift();
  }

  return {
    history: newHistory,
    historyIndex: newHistory.length - 1,
  };
}

export const useConfigurator = create<ConfiguratorStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentStep: 'scenario',
      config: createDefaultConfig(),
      isValid: true,
      errors: [],
      isDirty: false,
      history: [],
      historyIndex: -1,

      // Note: canGoNext, canGoBack, isFirstStep, isLastStep, currentStepIndex,
      // canUndo, canRedo are computed at the component level using selectors
      // to avoid issues with zustand persist middleware rehydration.

      // Navigation
      goToStep: (step) => {
        const { config } = get();
        if (canProceedToStep(step, config)) {
          set({ currentStep: step });
        }
      },

      goNext: () => {
        const { currentStep, config } = get();
        const currentIndex = WIZARD_STEPS.indexOf(currentStep);
        if (currentIndex < WIZARD_STEPS.length - 1 && isStepComplete(currentStep, config)) {
          set({ currentStep: WIZARD_STEPS[currentIndex + 1] });
        }
      },

      goBack: () => {
        const { currentStep } = get();
        const currentIndex = WIZARD_STEPS.indexOf(currentStep);
        if (currentIndex > 0) {
          set({ currentStep: WIZARD_STEPS[currentIndex - 1] });
        }
      },

      // Scenario
      setScenario: (scenario) => {
        const preset = SCENARIO_PRESETS.find((s) => s.id === scenario);
        if (!preset) return;

        const network = { ...DEFAULT_NETWORK_CONFIG };
        const nodes: DatacenterNode[] = [];

        // Create nodes from preset
        for (const nodePreset of preset.defaultNodes) {
          for (let i = 0; i < nodePreset.count; i++) {
            nodes.push(createNode(nodePreset.type, nodes, network, nodePreset.specs, nodePreset.role));
          }
        }

        // Create default service allocations
        const defaultServices = getDefaultServices();
        const serviceAllocations: ServiceAllocation[] = [];

        for (const service of defaultServices) {
          // Find a suitable node for this service
          const suitableNode = nodes.find((n) =>
            service.requiredNodeTypes.includes(n.type)
          );
          if (suitableNode) {
            serviceAllocations.push({
              serviceId: service.id,
              nodeId: suitableNode.id,
              enabled: true,
            });
          }
        }

        set((state) => ({
          ...saveSnapshot(state, `Set scenario: ${scenario}`),
          config: {
            ...state.config,
            scenario,
            nodes,
            serviceAllocations,
            estimatedCost: preset.estimatedCost,
            estimatedPower: preset.estimatedPower,
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      setFormFactor: (formFactor) => {
        set((state) => ({
          ...saveSnapshot(state, `Set form factor: ${formFactor}`),
          config: {
            ...state.config,
            formFactor,
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      // Network
      updateNetwork: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            network: { ...state.config.network, ...updates },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      // Nodes
      addNode: (type, specs, role) => {
        set((state) => {
          const newNode = createNode(type, state.config.nodes, state.config.network, specs, role);
          const nodes = [...state.config.nodes, newNode];
          return {
            ...saveSnapshot(state, `Add ${type} node`),
            config: {
              ...state.config,
              nodes,
              estimatedPower: calculateTotalPower(nodes),
              estimatedCost: calculateTotalCost(nodes),
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      updateNode: (id, updates) => {
        set((state) => {
          const nodes = state.config.nodes.map((n) =>
            n.id === id ? { ...n, ...updates } : n
          );
          return {
            config: {
              ...state.config,
              nodes,
              estimatedPower: calculateTotalPower(nodes),
              estimatedCost: calculateTotalCost(nodes),
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      removeNode: (id) => {
        set((state) => {
          const removedNode = state.config.nodes.find((n) => n.id === id);
          const nodes = state.config.nodes.filter((n) => n.id !== id);
          const serviceAllocations = state.config.serviceAllocations.filter(
            (a) => a.nodeId !== id
          );
          return {
            ...saveSnapshot(state, `Remove node ${removedNode?.hostname || id}`),
            config: {
              ...state.config,
              nodes,
              serviceAllocations,
              estimatedPower: calculateTotalPower(nodes),
              estimatedCost: calculateTotalCost(nodes),
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      duplicateNode: (id) => {
        const { config } = get();
        const original = config.nodes.find((n) => n.id === id);
        if (!original) return;

        set((state) => {
          const newNode = createNode(
            original.type,
            state.config.nodes,
            state.config.network,
            original.specs,
            original.role
          );
          newNode.name = `${original.name} (copy)`;
          const nodes = [...state.config.nodes, newNode];
          return {
            config: {
              ...state.config,
              nodes,
              estimatedPower: calculateTotalPower(nodes),
              estimatedCost: calculateTotalCost(nodes),
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      reorderNodes: (fromIndex, toIndex) => {
        set((state) => {
          if (fromIndex === toIndex) return state;
          if (fromIndex < 0 || fromIndex >= state.config.nodes.length) return state;
          if (toIndex < 0 || toIndex >= state.config.nodes.length) return state;

          const nodes = [...state.config.nodes];
          const [moved] = nodes.splice(fromIndex, 1);
          nodes.splice(toIndex, 0, moved);

          return {
            ...saveSnapshot(state, 'Reorder nodes'),
            config: {
              ...state.config,
              nodes,
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      // Services
      allocateService: (serviceId, nodeId) => {
        set((state) => {
          // Check if already allocated
          const exists = state.config.serviceAllocations.some(
            (a) => a.serviceId === serviceId && a.nodeId === nodeId
          );
          if (exists) return state;

          return {
            config: {
              ...state.config,
              serviceAllocations: [
                ...state.config.serviceAllocations,
                { serviceId, nodeId, enabled: true },
              ],
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      deallocateService: (serviceId, nodeId) => {
        set((state) => ({
          config: {
            ...state.config,
            serviceAllocations: state.config.serviceAllocations.filter(
              (a) => !(a.serviceId === serviceId && a.nodeId === nodeId)
            ),
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      toggleService: (serviceId, nodeId, enabled) => {
        set((state) => ({
          config: {
            ...state.config,
            serviceAllocations: state.config.serviceAllocations.map((a) =>
              a.serviceId === serviceId && a.nodeId === nodeId
                ? { ...a, enabled }
                : a
            ),
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      // Security
      updateSecurity: (updates) => {
        set((state) => ({
          ...saveSnapshot(state, 'Update security settings'),
          config: {
            ...state.config,
            security: { ...state.config.security, ...updates },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      addVPNPeer: (peer) => {
        set((state) => ({
          config: {
            ...state.config,
            security: {
              ...state.config.security,
              peers: [
                ...state.config.security.peers,
                { ...peer, id: generatePeerId() },
              ],
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      updateVPNPeer: (id, updates) => {
        set((state) => ({
          config: {
            ...state.config,
            security: {
              ...state.config.security,
              peers: state.config.security.peers.map((p) =>
                p.id === id ? { ...p, ...updates } : p
              ),
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      removeVPNPeer: (id) => {
        set((state) => ({
          config: {
            ...state.config,
            security: {
              ...state.config.security,
              peers: state.config.security.peers.filter((p) => p.id !== id),
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      // Container Management
      initializeContainer: (size) => {
        set((state) => {
          const containerConfig: ContainerConfig = {
            size,
            racks: [],
            infrastructure: [],
            airflowDirection: 'front-to-back',
          };

          return {
            ...saveSnapshot(state, 'Initialize container'),
            config: {
              ...state.config,
              containerConfig,
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      setContainerSize: (size) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            ...saveSnapshot(state, 'Change container size'),
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                size,
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      addContainerRack: (type) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          const dims = getRackDimensions(type);
          const existingRacks = state.config.containerConfig.racks;
          const rackNum = existingRacks.length + 1;

          // Position new rack with some offset from existing
          const xOffset = existingRacks.length * (dims.widthMm + 100);

          const newRack: ContainerRack = {
            id: generateRackId(),
            name: `Rack ${rackNum}`,
            type,
            position: { x: 200 + xOffset, y: 200 },
            rotation: 0,
            nodes: [],
          };

          return {
            ...saveSnapshot(state, 'Add container rack'),
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                racks: [...existingRacks, newRack],
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      updateContainerRack: (rackId, updates) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                racks: state.config.containerConfig.racks.map((rack) =>
                  rack.id === rackId ? { ...rack, ...updates } : rack
                ),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      removeContainerRack: (rackId) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            ...saveSnapshot(state, 'Remove container rack'),
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                racks: state.config.containerConfig.racks.filter((r) => r.id !== rackId),
                // Also remove infrastructure mounted in this rack
                infrastructure: state.config.containerConfig.infrastructure.filter(
                  (i) => i.rackId !== rackId
                ),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      moveContainerRack: (rackId, position) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                racks: state.config.containerConfig.racks.map((rack) =>
                  rack.id === rackId ? { ...rack, position } : rack
                ),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      assignNodeToRack: (nodeId, rackId) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          // First remove from any existing rack
          const updatedRacks = state.config.containerConfig.racks.map((rack) => ({
            ...rack,
            nodes: rack.nodes.filter((id) => id !== nodeId),
          }));

          // Then add to target rack
          const finalRacks = updatedRacks.map((rack) =>
            rack.id === rackId
              ? { ...rack, nodes: [...rack.nodes, nodeId] }
              : rack
          );

          return {
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                racks: finalRacks,
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      unassignNodeFromRack: (nodeId) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                racks: state.config.containerConfig.racks.map((rack) => ({
                  ...rack,
                  nodes: rack.nodes.filter((id) => id !== nodeId),
                })),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      addInfrastructure: (catalogId) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          const catalogItem = getInfrastructureById(catalogId);
          if (!catalogItem) return state;

          const existingInfra = state.config.containerConfig.infrastructure;
          const typeCount = existingInfra.filter((i) => i.type === catalogItem.type).length;

          // Position new infrastructure
          const xOffset = existingInfra.length * 150;

          const newInfra: InfrastructureUnit = {
            id: generateInfrastructureId(),
            type: catalogItem.type,
            name: `${catalogItem.name} ${typeCount + 1}`,
            specs: { ...catalogItem.specs },
            position: { x: 100 + xOffset, y: 1800 },
          };

          return {
            ...saveSnapshot(state, `Add ${catalogItem.type}`),
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                infrastructure: [...existingInfra, newInfra],
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      updateInfrastructure: (id, updates) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                infrastructure: state.config.containerConfig.infrastructure.map((infra) =>
                  infra.id === id ? { ...infra, ...updates } : infra
                ),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      removeInfrastructure: (id) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            ...saveSnapshot(state, 'Remove infrastructure'),
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                infrastructure: state.config.containerConfig.infrastructure.filter(
                  (i) => i.id !== id
                ),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      moveInfrastructure: (id, position) => {
        set((state) => {
          if (!state.config.containerConfig) return state;

          return {
            config: {
              ...state.config,
              containerConfig: {
                ...state.config.containerConfig,
                infrastructure: state.config.containerConfig.infrastructure.map((infra) =>
                  infra.id === id ? { ...infra, position } : infra
                ),
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      // Network Segment Management
      addNetworkSegment: (type) => {
        set((state) => {
          const preset = NETWORK_SEGMENT_PRESETS[type];
          const existingOfType = state.config.network.segments.filter((s) => s.type === type);

          // If this type already exists, adjust the subnet
          let subnet = preset.subnet;
          let gateway = preset.gateway;
          let vlanId = preset.vlanId;

          if (existingOfType.length > 0) {
            // Increment the third octet and VLAN ID for additional segments
            const offset = existingOfType.length;
            const parts = preset.subnet.split('.');
            const newOctet = parseInt(parts[2], 10) + offset * 10;
            subnet = `${parts[0]}.${parts[1]}.${newOctet}.0/24`;
            gateway = `${parts[0]}.${parts[1]}.${newOctet}.1`;
            vlanId = (preset.vlanId || 200) + offset;
          }

          const newSegment: NetworkSegment = {
            ...preset,
            id: generateSegmentId(),
            subnet,
            gateway,
            vlanId,
          };

          return {
            ...saveSnapshot(state, `Add ${type} network`),
            config: {
              ...state.config,
              network: {
                ...state.config.network,
                segments: [...state.config.network.segments, newSegment],
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      updateNetworkSegment: (id, updates) => {
        set((state) => ({
          config: {
            ...state.config,
            network: {
              ...state.config.network,
              segments: state.config.network.segments.map((seg) =>
                seg.id === id ? { ...seg, ...updates } : seg
              ),
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      removeNetworkSegment: (id) => {
        set((state) => ({
          ...saveSnapshot(state, 'Remove network segment'),
          config: {
            ...state.config,
            network: {
              ...state.config.network,
              segments: state.config.network.segments.filter((s) => s.id !== id),
              // Also remove node assignments for this segment
              nodeAssignments: state.config.network.nodeAssignments.filter(
                (a) => a.segmentId !== id
              ),
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      assignNodeToNetwork: (nodeId, segmentId, ip, isPrimary = false) => {
        set((state) => {
          const existingAssignments = state.config.network.nodeAssignments || [];

          // Check if already assigned
          const alreadyAssigned = existingAssignments.some(
            (a) => a.nodeId === nodeId && a.segmentId === segmentId
          );
          if (alreadyAssigned) return state;

          // If setting as primary, unset other primaries for this node
          const updatedAssignments = isPrimary
            ? existingAssignments.map((a) =>
                a.nodeId === nodeId ? { ...a, isPrimary: false } : a
              )
            : existingAssignments;

          const newAssignment: NodeNetworkAssignment = {
            nodeId,
            segmentId,
            ip,
            isPrimary,
          };

          return {
            config: {
              ...state.config,
              network: {
                ...state.config.network,
                nodeAssignments: [...updatedAssignments, newAssignment],
              },
              updatedAt: Date.now(),
            },
            isDirty: true,
          };
        });
      },

      unassignNodeFromNetwork: (nodeId, segmentId) => {
        set((state) => ({
          config: {
            ...state.config,
            network: {
              ...state.config.network,
              nodeAssignments: state.config.network.nodeAssignments.filter(
                (a) => !(a.nodeId === nodeId && a.segmentId === segmentId)
              ),
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      updateNodeNetworkIP: (nodeId, segmentId, ip) => {
        set((state) => ({
          config: {
            ...state.config,
            network: {
              ...state.config.network,
              nodeAssignments: state.config.network.nodeAssignments.map((a) =>
                a.nodeId === nodeId && a.segmentId === segmentId
                  ? { ...a, ip }
                  : a
              ),
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      setSubscriptionModel: (enabled, providerName, customerName) => {
        set((state) => ({
          ...saveSnapshot(state, 'Update subscription model'),
          config: {
            ...state.config,
            network: {
              ...state.config.network,
              subscriptionModel: {
                enabled,
                providerName,
                customerName,
              },
            },
            updatedAt: Date.now(),
          },
          isDirty: true,
        }));
      },

      // General
      updateConfig: (updates) => {
        set((state) => ({
          config: { ...state.config, ...updates, updatedAt: Date.now() },
          isDirty: true,
        }));
      },

      loadConfig: (config) => {
        // Recalculate costs when loading
        const estimatedPower = calculateTotalPower(config.nodes);
        const estimatedCost = calculateTotalCost(config.nodes);

        set({
          currentStep: 'summary',
          config: {
            ...config,
            estimatedPower,
            estimatedCost,
            updatedAt: Date.now(),
          },
          isDirty: false,
        });
      },

      reset: () => {
        set({
          currentStep: 'scenario',
          config: createDefaultConfig(),
          isValid: true,
          errors: [],
          isDirty: false,
        });
      },

      validate: () => {
        // NOTE: This function is pure and does NOT call set() to avoid
        // infinite render loops when called during component render.
        // Use the returned errors directly instead of subscribing to state.errors
        const { config } = get();
        const errors: ValidationError[] = [];

        // === NODE VALIDATION ===

        // Check for gateway
        if (!config.nodes.some((n) => n.type === 'gateway')) {
          errors.push({
            field: 'nodes.gateway',
            message: 'At least one gateway node is required',
            severity: 'error',
          });
        }

        // Check for duplicate IPs
        const ips = config.nodes.map((n) => n.ip);
        const duplicateIPs = ips.filter((ip, i) => ips.indexOf(ip) !== i);
        if (duplicateIPs.length > 0) {
          errors.push({
            field: 'nodes.ip',
            message: `Duplicate IP addresses: ${[...new Set(duplicateIPs)].join(', ')}`,
            severity: 'error',
          });
        }

        // Check for duplicate hostnames
        const hostnames = config.nodes.map((n) => n.hostname);
        const duplicateHostnames = hostnames.filter((h, i) => hostnames.indexOf(h) !== i);
        if (duplicateHostnames.length > 0) {
          errors.push({
            field: 'nodes.hostname',
            message: `Duplicate hostnames: ${[...new Set(duplicateHostnames)].join(', ')}`,
            severity: 'error',
          });
        }

        // Check node count
        if (config.nodes.length === 0) {
          errors.push({
            field: 'nodes',
            message: 'No nodes configured',
            severity: 'error',
          });
        }

        // Check for high memory usage warning
        const totalMemory = config.nodes.reduce((sum, n) => sum + n.specs.memoryGB, 0);
        const allocatedMemory = config.serviceAllocations.reduce((sum, a) => {
          const service = SERVICES.find((s) => s.id === a.serviceId);
          return sum + (service?.memoryMB || 0) / 1024;
        }, 0);
        if (totalMemory > 0 && allocatedMemory / totalMemory > 0.9) {
          errors.push({
            field: 'services.memory',
            message: 'Services use over 90% of total cluster memory',
            severity: 'warning',
          });
        }

        // === SERVICE VALIDATION ===

        // Check critical services are allocated
        const criticalServices = SERVICES.filter((s) => s.critical);
        for (const service of criticalServices) {
          const allocated = config.serviceAllocations.some(
            (a) => a.serviceId === service.id && a.enabled
          );
          if (!allocated) {
            errors.push({
              field: 'services.critical',
              message: `Critical service "${service.name}" is not allocated`,
              severity: 'warning',
            });
          }
        }

        // Check for unallocated services
        const unallocatedCount = SERVICES.filter(
          (s) => !config.serviceAllocations.some((a) => a.serviceId === s.id)
        ).length;
        if (unallocatedCount > 5) {
          errors.push({
            field: 'services',
            message: `${unallocatedCount} services are not allocated to any node`,
            severity: 'info',
          });
        }

        // === NETWORK VALIDATION ===

        // Check if gateway IP is within subnet
        const subnet = config.network.subnet.split('/')[0];
        const subnetParts = subnet.split('.').slice(0, 3).join('.');
        const gatewayParts = config.network.gateway.split('.').slice(0, 3).join('.');
        if (subnetParts !== gatewayParts) {
          errors.push({
            field: 'network.gateway',
            message: 'Gateway IP is not within the configured subnet',
            severity: 'error',
          });
        }

        // === SECURITY VALIDATION ===

        // Check VPN peers if VPN is enabled
        if (config.security.vpnEnabled && config.security.peers.length === 0) {
          errors.push({
            field: 'security.vpn',
            message: 'VPN is enabled but no peers are configured',
            severity: 'warning',
          });
        }

        // Check for empty VPN public keys
        if (config.security.vpnEnabled) {
          const peersWithoutKeys = config.security.peers.filter((p) => p.enabled && !p.publicKey);
          if (peersWithoutKeys.length > 0) {
            errors.push({
              field: 'security.peers',
              message: `${peersWithoutKeys.length} enabled peer(s) missing public key`,
              severity: 'warning',
            });
          }
        }

        // Security recommendations (info severity)
        if (!config.security.firewallDefaultDeny) {
          errors.push({
            field: 'security.firewall',
            message: 'Default-deny firewall is recommended for airgapped networks',
            severity: 'info',
          });
        }

        if (!config.security.auditLogging) {
          errors.push({
            field: 'security.audit',
            message: 'Audit logging is recommended for compliance',
            severity: 'info',
          });
        }

        return errors;
      },

      getStepErrors: (step: WizardStep) => {
        const errors = get().validate();
        const stepFieldPrefixes: Record<WizardStep, string[]> = {
          scenario: ['scenario'],
          network: ['network'],
          nodes: ['nodes'],
          services: ['services'],
          security: ['security'],
          summary: [], // Summary shows all errors
        };

        if (step === 'summary') {
          return errors;
        }

        const prefixes = stepFieldPrefixes[step];
        return errors.filter(
          (e) => e.field && prefixes.some((p) => e.field!.startsWith(p))
        );
      },

      // History / Undo-Redo
      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex > 0) {
          const prevSnapshot = history[historyIndex - 1];
          set({
            config: JSON.parse(JSON.stringify(prevSnapshot.config)),
            historyIndex: historyIndex - 1,
            isDirty: true,
          });
        }
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const nextSnapshot = history[historyIndex + 1];
          set({
            config: JSON.parse(JSON.stringify(nextSnapshot.config)),
            historyIndex: historyIndex + 1,
            isDirty: true,
          });
        }
      },

      getLastChanges: () => {
        const { history, historyIndex, config } = get();
        if (historyIndex < 0 || history.length === 0) {
          return [];
        }
        const prevConfig = history[historyIndex]?.config;
        if (!prevConfig) {
          return [];
        }
        return diffConfigs(prevConfig, config);
      },

      createSnapshot: (label?: string) => {
        set((state) => saveSnapshot(state, label));
      },
    }),
    {
      name: 'hermit-configurator',
      partialize: (state) => ({
        config: state.config,
        history: state.history.slice(-10), // Only persist last 10 snapshots
        historyIndex: Math.min(state.historyIndex, 9),
      }),
      // Handle corrupted localStorage gracefully
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Failed to rehydrate state from localStorage:', error);
          // Clear corrupted data
          try {
            localStorage.removeItem('hermit-configurator');
          } catch {
            // Ignore localStorage errors
          }
        }
        // Validate rehydrated state
        if (state?.config && !state.config.network) {
          console.warn('Invalid config in localStorage, resetting');
          state.config = createDefaultConfig();
        }
        // Migrate configs without network segments
        if (state?.config?.network && !state.config.network.segments) {
          state.config.network.segments = [];
          state.config.network.nodeAssignments = [];
          state.config.network.subscriptionModel = { enabled: false };
        }
      },
    }
  )
);
